import { type FC, useState } from "react";
import type { BulkRevocationResponse } from "../../../shared/models/bulk_revocation";
import { bulkRevokeCertificates } from "../stores/certificate_store";

interface BulkRevokeModalProps {
  certificates: { deviceId: string; certvalidfrom: string }[];
  onClose: () => void;
  onComplete: (result: BulkRevocationResponse) => void;
}

export const BulkRevokeModal: FC<BulkRevokeModalProps> = ({
  certificates,
  onClose,
  onComplete,
}) => {
  const [confirmText, setConfirmText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmEnabled = confirmText.trim() === "REVOKE" && !isProcessing;

  const handleConfirm = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const result = await bulkRevokeCertificates(certificates);
      onComplete(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
      setIsProcessing(false);
    }
  };

  return (
    <div className="vmx-modal" style={{ maxWidth: 420 }}>
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">Bulk Revoke Certificates</div>
      </div>
      <div className="vmx-modal-body">
        {error ? (
          <p style={{ color: "var(--vmx-danger)", lineHeight: 1.6 }}>{error}</p>
        ) : (
          <p style={{ color: "var(--vmx-gray-600)", lineHeight: 1.6 }}>
            You are about to revoke <strong>{certificates.length}</strong>{" "}
            certificate
            {certificates.length === 1 ? "" : "s"}. This action cannot be
            undone. Type <strong>REVOKE</strong> to confirm.
          </p>
        )}
        <input
          className="vmx-input"
          type="text"
          placeholder='Type "REVOKE" to confirm'
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={isProcessing}
          style={{ marginTop: 12, width: "100%" }}
        />
      </div>
      <div className="vmx-modal-footer">
        {error ? (
          <>
            <button className="vmx-btn vmx-btn-secondary" onClick={onClose}>
              Dismiss
            </button>
            <button
              className="vmx-btn vmx-btn-danger"
              onClick={handleConfirm}
              disabled={!isConfirmEnabled}
            >
              Retry
            </button>
          </>
        ) : (
          <>
            <button
              className="vmx-btn vmx-btn-secondary"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              className="vmx-btn vmx-btn-danger"
              onClick={handleConfirm}
              disabled={!isConfirmEnabled}
            >
              {isProcessing
                ? `Revoking ${certificates.length} certificates...`
                : "Revoke"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
